// @owners { team: patient-health }
import { AltoIcon, Row, XxsSpacing } from '@alto/design-system';
// eslint-disable-next-line @alto/no-pocky-import
import { H3, LoaderImage, Text, Tooltip, palette, spinnerStyles } from '@alto/pocky';
import { faShieldAlt } from '@fortawesome/pro-light-svg-icons/faShieldAlt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { type RefObject } from 'react';
import styled from 'styled-components';
import { PRESCRIPTION_PRICING_LABELS } from '~shared/features/pricing/constants';
import { parseDollarsToNumber } from '~shared/helpers/currency';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { createEvent } from '~shared/lib/analytics/src/helper';
import { type Event } from '~shared/lib/analytics/src/types';
import { type Prescription } from '~shared/types';
import { useTrackNodeSeen } from '~web/hooks';

const AttrIcon = styled(FontAwesomeIcon)`
  margin-right: 0.75rem;
`;

export type Props = {
  readonly prescription: Prescription;
  readonly paymentLabel: string | null | undefined;
  readonly price: string | null | undefined;
  readonly priceLabel: string | null | undefined;
  readonly priceLabelInfo: string | null | undefined;
  readonly priceLoading?: boolean;
  readonly sendAnalyticsEvent?: (arg0: Event) => void;
  readonly small?: boolean;
  readonly deliveryID?: number | undefined;
};

const uncenteredSpinnerStyles = { ...spinnerStyles, margin: 'unset' };

const Container = styled.div`
  flex: 1;
  text-transform: none;
`;

const Price = styled(H3)`
  display: inline;
  line-height: 1rem;
`;

const MedListPrice = ({
  paymentLabel,
  price,
  priceLabel,
  priceLabelInfo,
  prescription,
  sendAnalyticsEvent,
  small,
  priceLoading,
  deliveryID,
}: Props) => {
  const refNode = useTrackNodeSeen({
    onSeen: () => {
      if (!sendAnalyticsEvent) return;
      sendAnalyticsEvent(
        createEvent(
          EVENTS.PRICE_INFO_VIEWED,
          {
            price: parseDollarsToNumber(price),
            price_type: priceLabel,
            origin: ORIGIN_NAMES.MED_LIST,
          },
          { prescriptionId: prescription.id, deliveryId: deliveryID },
        ),
      );
    },
  });

  if (priceLoading) {
    return <LoaderImage style={uncenteredSpinnerStyles} />;
  }

  const showPriceInfo = !!priceLabelInfo;
  const PriceText = small ? Text : Price;

  return (
    <Container>
      <Row wrap>
        <PriceText ref={refNode as RefObject<any>}>
          {!price ? PRESCRIPTION_PRICING_LABELS.VIEW_PRICE_IN_CART : priceLabel?.concat(': ')}
          {price}
        </PriceText>
        <XxsSpacing />
        {showPriceInfo ? (
          <Tooltip content={priceLabelInfo}>
            <AltoIcon
              name="info-small"
              accessibilityLabel="Hover for price information"
            />
          </Tooltip>
        ) : null}
      </Row>
      {paymentLabel ? (
        <div>
          <AttrIcon
            color={palette.grey1}
            icon={faShieldAlt}
          />
          <Text
            small
            inline
          >
            Insurance: {paymentLabel}
          </Text>
        </div>
      ) : null}
    </Container>
  );
};

export default MedListPrice;
